import { BehaviorSubject } from "rxjs"

import { parseDuration, ShorthandDuration } from "@treefort/lib/parse-duration"

import { Store } from "./store"

const PLAYBACK_TIMER_KEY = "playback-timer"

export type PlaybackTimer = {
  createdAt: number
} & PlaybackTimerDataInput

type PlaybackTimerDataInput =
  | {
      type: "time"
      duration: ShorthandDuration
    }
  | {
      type: "milestone"
      milestone: "endOfChapter"
    }

const store = new Store({
  key: "playback-timer",
})

export function isExpired(playbackTimer?: PlaybackTimer | null) {
  return (
    playbackTimer?.type === "time" &&
    new Date().getTime() - playbackTimer.createdAt >
      parseDuration(playbackTimer.duration)
  )
}

function storeSetter(setFn: (timer: PlaybackTimer | null) => Promise<void>) {
  return async (timerData: PlaybackTimerDataInput | null) => {
    const playbackTimer = timerData
      ? { ...timerData, createdAt: new Date().getTime() }
      : null

    await setFn(playbackTimer)
    return playbackTimer
  }
}

export const playbackTimerObservable =
  new BehaviorSubject<PlaybackTimer | null>(null)

export const playbackTimerStore = {
  get: async function () {
    return await store.get<PlaybackTimer | null>(PLAYBACK_TIMER_KEY)
  },
  set: storeSetter(async (timer) => {
    await store.set(PLAYBACK_TIMER_KEY, timer)
    playbackTimerObservable.next(timer)
  }),
  clear: () => {
    void store.remove(PLAYBACK_TIMER_KEY)
    playbackTimerObservable.next(null)
  },
  pauseIfExpired: async function (pauseFunc: () => Promise<void>) {
    if (!isExpired(await store.get(PLAYBACK_TIMER_KEY))) {
      return false
    }

    await Promise.all([this.clear(), pauseFunc()])
    return true
  },
}

// Initialize the observable with an existing timer, if present
playbackTimerStore.get().then((value) => {
  if (value) {
    playbackTimerObservable.next(value)
  }
})
